import { nextTick } from "vue";
import posthog from "posthog-js";
import trackedEvents, {
  isValidEvent,
  viewContentPageIds,
} from "@/utils/trackedEvents";
import { DEVICE_SPECS, getDisplayMode } from "@/utils/helpers.js";
import sessionTracker from "@/services/SessionTracker.js";
import config from "@/config";

/**
 * @type posthog
 */
let posthogInstance;

export const initialize = (apiKey, apiHost) => {
  posthogInstance = posthog.init(apiKey, {
    api_host: apiHost,
    ui_host: "https://us.posthog.com",
    person_profiles: "identified_only",
    capture_pageview: false,
    disable_session_recording: true,
    defaults: "2025-05-24",
    before_send: (event) => {
      if (event.event === "$exception") {
        const exceptionList = event.properties.$exception_list || [];
        const exception = exceptionList.length > 0 ? exceptionList[0] : null;

        if (
          exception &&
          exception.$exception_type === "UnhandledRejection" &&
          exception.$exception_message.includes(
            "Failed to fetch dynamically imported module"
          )
        ) {
          return false;
        }
      }

      if (event.event === "survey sent") {
        const surveyId = event.properties.$survey_id;
        if (
          surveyId === config.posthog.interviewSurveyId &&
          event.properties.$survey_questions[2].response
        ) {
          setTimeout(() => {
            window.location.href = "https://sababuu.com";
          }, 100); // Small delay to ensure the event is sent
        }
      }
      return event;
    },
  });
  // posthogInstance.featureFlags.overrideFeatureFlags({
  //   flags: {},
  // });
};

export const trackEvent = (event, properties) => {
  if (!isValidEvent(event)) {
    // console.warn("Posthog skipping event -> ", event);
    return;
  }

  if (posthogInstance) {
    // console.log("Posthog -> ", event, properties);
    posthogInstance.capture(event, properties);

    if (event === trackedEvents.LOGGED_OUT) {
      posthogInstance.reset();
    }
  } else {
    console.warn("Posthog analytics is not initialized");
  }

  if (window.SababuuNativeBridge?.firebaseLogEvent) {
    const { mappedFirebaseEvent, firebaseEventValue = {} } = mapToFirebaseROAS(
      event,
      properties
    );
    window.SababuuNativeBridge.firebaseLogEvent(mappedFirebaseEvent, {
      ...properties,
      ...firebaseEventValue,
    });
  }

  if (window.SababuuNativeBridge?.metaLogEvent) {
    const { mappedMetaEvent = null, metaEventProperties = {} } =
      mapToMetaEvent(event);
    if (mappedMetaEvent) {
      window.SababuuNativeBridge.metaLogEvent(
        mappedMetaEvent,
        metaEventProperties
      );
    }
  }

  if (window.SababuuNativeBridge?.tikTokLogEvent) {
    const { mappedTikTokEvent = null, tikTokEventProperties = {} } =
      mapToTikTokEvent(event, properties);
    if (mappedTikTokEvent) {
      window.SababuuNativeBridge.tikTokLogEvent(
        mappedTikTokEvent,
        tikTokEventProperties
      );
    }
  }
};

function mapToFirebaseROAS(event, properties) {
  if (event === trackedEvents.ACCOUNT_CREATED) {
    const { gender } = properties;
    return {
      mappedFirebaseEvent: `${trackedEvents.ACCOUNT_CREATED}_${gender}`,
      firebaseEventValue: {
        value: gender === "female" ? 0.075 : 0.025,
        currency: "EUR",
      },
    };
  }

  return {
    mappedFirebaseEvent: event,
  };
}

function mapToMetaEvent(event) {
  if (event === trackedEvents.ACCOUNT_CREATED) {
    return {
      mappedMetaEvent: "CompleteRegistration",
      metaEventProperties: {
        registrationMethod: "phoneNumber",
      },
    };
  }

  const pageId = viewContentPageIds[event];
  if (pageId) {
    return {
      mappedMetaEvent: "ViewedContent",
      metaEventProperties: {
        contentType: "page",
        contentId: pageId,
      },
    };
  }

  return {};
}

function mapToTikTokEvent(event, properties) {
  if (event === trackedEvents.ACCOUNT_CREATED) {
    return {
      mappedTikTokEvent: "Registration",
      tikTokEventProperties: {
        gender: properties.gender === "female" ? "f" : "m",
      },
    };
  }

  if (event === trackedEvents.LOGGED_IN && properties.gender) {
    return {
      mappedTikTokEvent: "Login",
      tikTokEventProperties: {
        gender: properties.gender === "female" ? "f" : "m",
      },
    };
  }

  const pageId = viewContentPageIds[event];
  if (pageId) {
    return {
      mappedTikTokEvent: "ViewContent",
      tikTokEventProperties: {
        contentType: "page",
        contentId: pageId,
      },
    };
  }

  return {};
}

export const startSessionRecording = () => {
  if (posthogInstance) {
    posthogInstance.startSessionRecording();
  } else {
    console.warn("Posthog analytics is not initialized");
  }
};

export const getPosthogPerson = (user) => {
  return {
    userId: user.id,
    phone: user.phone,
    country: user.country,
    createdAt: user.created_at,
    gender: user.gender,
    birthday: user.birthday,
    profileVerified: user.person_verification,
    referralCode: user.referral_code,
  };
};

export const identifyUser = (
  userId,
  traits,
  { startRecording, registerCompleted } = {
    startRecording: false,
    registerCompleted: false,
  }
) => {
  if (posthogInstance) {
    // console.log("Posthog identifying -> ", userId, traits);
    posthogInstance.identify(userId, {
      display_mode: getDisplayMode(),
      device_specs: DEVICE_SPECS,
      onboarding_completed: registerCompleted,
      ...traits,
    });
    if (startRecording) {
      startSessionRecording();
    }
  } else {
    console.warn("Posthog analytics is not initialized");
  }
};

export const resetPosthogUser = () => {
  if (posthogInstance) {
    posthogInstance.reset();
  }
};

export const isFeatureEnabled = (feature) => {
  return new Promise((resolve) => {
    if (posthogInstance) {
      posthogInstance.onFeatureFlags((_a, _b, { errorsLoading } = {}) => {
        if (errorsLoading) {
          console.error(
            "Posthog isFeatureEnabled->onFeatureFlags failed to load"
          );
          resolve(false);
        } else {
          try {
            resolve(posthog.isFeatureEnabled(feature));
          } catch (err) {
            console.error("Posthog isFeatureEnabled failed with err -> ", err);
            resolve(false);
          }
        }
      });
    } else {
      console.warn("Posthog analytics is not initialized");
      resolve(false);
    }
  });
};

export const getFeatureFlag = (feature) => {
  return new Promise((resolve) => {
    if (posthogInstance) {
      posthogInstance.onFeatureFlags((_a, _b, { errorsLoading } = {}) => {
        if (errorsLoading) {
          console.error(
            "Posthog getFeatureFlag->onFeatureFlags failed to load"
          );
          resolve(undefined);
        } else {
          try {
            const flag = posthog.getFeatureFlag(feature);
            resolve(flag);
          } catch (err) {
            console.error("Posthog getFeatureFlag failed with err -> ", err);
            resolve(undefined);
          }
        }
      });
    } else {
      console.warn("Posthog analytics is not initialized");
      resolve(undefined);
    }
  });
};

export const captureException = (error, opts = null) => {
  console.error(error);
  if (posthogInstance) {
    if (opts) {
      posthogInstance.captureException(error, opts);
    } else {
      posthogInstance.captureException(error);
    }
  } else {
    console.warn("Posthog analytics is not initialized");
  }
};

export const getActiveMatchingSurveys = () => {
  return new Promise((resolve, reject) => {
    if (posthogInstance) {
      posthogInstance.onSurveysLoaded(() => {
        posthogInstance.getActiveMatchingSurveys((surveys) => {
          resolve(surveys);
        }, true);
      });
    } else {
      console.warn("Posthog analytics is not initialized");
      reject(new Error("PostHog not initialized"));
    }
  });
};

export const answerSurvey = (properties) => {
  if (posthogInstance) {
    posthogInstance.capture("survey sent", properties);
  } else {
    console.warn("Posthog analytics is not initialized");
  }
};

export const dismissedSurvey = (properties) => {
  if (posthogInstance) {
    posthogInstance.capture("survey dismissed", properties);
  } else {
    console.warn("Posthog analytics is not initialized");
  }
};

export const shownSurvey = (properties) => {
  if (posthogInstance) {
    posthogInstance.capture("survey shown", properties);
  } else {
    console.warn("Posthog analytics is not initialized");
  }
};

export default {
  install(app, options) {
    if (!options.apiKey || !options.apiHost) {
      console.error("Posthog apiKey and apiHost are required");
      return;
    }

    initialize(options.apiKey, options.apiHost);

    app.config.globalProperties.$posthog = posthogInstance;

    if (options.router) {
      options.router.beforeEach((_to, from) => {
        app.config.globalProperties.$posthog.capture("$pageleave", {
          display_mode: getDisplayMode(),
          $session_duration: sessionTracker.getSessionDuration(),
          path: from.fullPath,
        });
        return true;
      });

      options.router.afterEach((to, _from, failure) => {
        if (!failure) {
          nextTick(() => {
            app.config.globalProperties.$posthog.capture("$pageview", {
              display_mode: getDisplayMode(),
              $session_duration: sessionTracker.getSessionDuration(),
              path: to.fullPath,
            });
          });
        }
      });
    }
  },
};
